import { useCallback, useMemo } from 'react'
import { postApp } from '$services/api'
import { useRequest } from 'ahooks'
import { toast } from 'sonner'
import { redirect, ROUTE_NAMES } from '$router/config'
import CreateAppForm from './CreateAppForm'
import { TField } from './constants'
import GeneratePublicKeyPrompt from './GeneratePublicKeyPrompt'
import { usePublicKeyService } from '$hooks/services/usePublicKeyService'
import { useAppDetailStore } from '$hooks/stores'

import { container } from './styles.css'
import usePageClass from '$components/Modal/hooks/usePageClass'

enum STATE {
  CREATE_APP,
  GENERATE_PUBLIC_KEY
}

const CreateApplication = () => {
  const {
    run: saveApp,
    loading: savingApp,
    data
  } = useRequest(postApp, {
    manual: true,
    onSuccess: () => {
      toast.success(`You've created app successfully!`)
    }
  })

  usePageClass({ prefix: 'background-low', name: 'app-user-assign-roles-page' })

  const { generatePublicKeyAsync, generatingPublicKey } = usePublicKeyService()

  const updatePublicKey = useAppDetailStore(state => state.updatePublicKey)

  const { id: appId, client_id } = { ...data?.data }

  const state: STATE = useMemo(() => {
    if (client_id) return STATE.GENERATE_PUBLIC_KEY
    return STATE.CREATE_APP
  }, [client_id])

  const onCreate = useCallback(
    ({ key, environment, ...payload }: TField) => {
      saveApp({
        ...payload,
        logo: { key },
        environment: +environment.value
      })
    },
    [saveApp]
  )

  const onRedirectAppBasicSetting = useCallback(() => {
    redirect(ROUTE_NAMES.APP_BASIC_SETTING, ({ params, ...options }) => ({
      ...options,
      params: { ...params, appId }
    }))
  }, [appId])

  const onGenerate = useCallback(() => {
    if (!client_id) return
    generatePublicKeyAsync({ client_id, force: false }).then(({ data }) => {
      updatePublicKey(data.public_key)
      onRedirectAppBasicSetting()
    })
  }, [
    client_id,
    generatePublicKeyAsync,
    updatePublicKey,
    onRedirectAppBasicSetting
  ])

  return (
    <article className={container}>
      {state === STATE.CREATE_APP && (
        <CreateAppForm loading={savingApp} onSubmit={onCreate} />
      )}

      {state === STATE.GENERATE_PUBLIC_KEY && (
        <GeneratePublicKeyPrompt
          onGenerate={onGenerate}
          onNotNow={onRedirectAppBasicSetting}
          loading={generatingPublicKey}
        />
      )}
    </article>
  )
}

export default CreateApplication
